/**
 * Locale detection
 *
 * Resolves which language the app should render in, from the user's stored
 * override and the device's language settings.
 *
 * Resolution order:
 *   1. stored override ('en' | 'es') — set by the Settings language picker
 *   2. device locale, when the override is 'system' or unset
 *   3. DEFAULT_LANGUAGE
 *
 * Region variants collapse to their base language, so es-MX, es-419 and es-US
 * all resolve to 'es'. Adding a locale later means appending to
 * SUPPORTED_LANGUAGES and dropping in a catalog — no change to this logic.
 */

import { getLocales } from 'expo-localization';

/** Languages with a translation catalog. Order is not significant. */
export const SUPPORTED_LANGUAGES = ['en', 'es'];

/** Rendered when nothing else resolves, and the fallback for missing keys. */
export const DEFAULT_LANGUAGE = 'en';

/** Stored-preference sentinel meaning "follow the device". */
export const SYSTEM_LANGUAGE = 'system';

/**
 * Reduce a locale tag to a supported base language.
 *
 * @param {string} tag - a locale tag or language code ('es', 'es-MX', 'es_419')
 * @returns {string|null} the supported base language, or null if unsupported
 */
export function normalizeLanguage(tag) {
  if (!tag || typeof tag !== 'string') return null;

  const base = tag.toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : null;
}

/**
 * The best supported language among the device's preferred locales.
 *
 * Walks the full preference list rather than reading only the first entry: a
 * user whose order is [fr-CA, es-MX, en-US] gets Spanish rather than falling
 * straight through to English on the unsupported first choice.
 *
 * @returns {string} a supported language code
 */
export function getDeviceLanguage() {
  try {
    const locales = getLocales();
    if (Array.isArray(locales)) {
      for (const locale of locales) {
        const match = normalizeLanguage(locale?.languageCode || locale?.languageTag);
        if (match) return match;
      }
    }
  } catch (error) {
    // expo-localization throws if the native module is unavailable. Rendering
    // in English beats failing to render, so swallow and fall through.
    console.warn('Locale detection failed; defaulting language:', error?.message || error);
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Resolve the language to render in.
 *
 * @param {string} [stored] - the persisted override ('en' | 'es' | 'system')
 * @returns {string} a supported language code
 */
export function resolveLanguage(stored) {
  if (stored && stored !== SYSTEM_LANGUAGE) {
    const match = normalizeLanguage(stored);
    if (match) return match;
  }

  return getDeviceLanguage();
}
